import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import useGenerateFields, { Field } from '../../../hook/useGenerateFields';
import MainSection from '../../../components/Core/MainSection/MainSection';
import Card from '../../../components/Core/Card/Card';
import Button from '../../../components/Core/Button/Button';
import { instance } from '../../../api/user.api';
import { selectCollAndDeliveryPointID } from '../../../auth/selectors/authSelectors';
import { useSnackbar } from '../../../context/SnackbarContext';
import { TOAST_SEVERITY } from '../../../components/Core/Toast/Toast';
import { CollectivityAdminParamsResponse } from './CollectivityAdminParams.layout';
import styles from './AlwinParameters.module.scss';

type AlwinRawBodyForm = { alwinAuthPassword: string | null; alwinAuthUser: string; alwinSupervisorCodeId: number };

/*function transformRawDataToBodyForm({
  alwinAuthPassword: alwinauthpwd_coll,
  alwinSupervisorCodeId: alwinsupervisorID_coll,
  alwinAuthUser: alwinauthuser_coll,
  ...paramsToForward
}: AlwinRawBodyForm) {
  return { alwinauthpwd_coll, alwinauthuser_coll, alwinsupervisorID_coll, ...paramsToForward };
}*/

function useAlwinForm() {
  const { t } = useTranslation(['admin/collectivityAdmin']);
  return useMemo<Field[]>(() => {
    return [
      {
        type: 'text',
        label: t('alwin_id'),
        name: 'alwinAuthUser',
      },
      {
        type: 'text',
        label: t('alwin_pass'),
        name: 'alwinAuthPassword',
      },
      {
        type: 'number',
        label: t('alwin_code'),
        name: 'alwinSupervisorCodeId',
        // schema: Joi.number().allow(null).allow(''),
      },
    ];
  }, [t]);
}

function AlwinParametersPage() {
  const { t } = useTranslation(['form']);
  const { id_coll } = useSelector(selectCollAndDeliveryPointID);
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const collectivityAdminParamsResponse = useOutletContext<
    AxiosResponse<CollectivityAdminParamsResponse> | undefined
  >();
  const defaultValues = useMemo(() => {
    if (collectivityAdminParamsResponse) {
      const {
        data: {
          admin: { alwinAuthPassword, alwinAuthUser, alwinSupervisorCodeId },
        },
      } = collectivityAdminParamsResponse;
      return { alwinAuthPassword, alwinAuthUser, alwinSupervisorCodeId };
    }
  }, [collectivityAdminParamsResponse]);
  const { control, handleSubmit } = useForm({ defaultValues });
  const fields = useAlwinForm();
  //eslint-disable-next-line
  const form = useGenerateFields({ fields, control: control as any });

  const { mutate: postAlwinParams } = useMutation(
    ['param/admin'],
    (bodyFormRaw: AlwinRawBodyForm & { id_coll: number }) => {
      return instance.post('param/admin', bodyFormRaw);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['param/admin']);
        snackbar?.setAlert({ message: t('form:global_update_success_message'), severity: TOAST_SEVERITY.success });
      },
    }
  );

  const onSubmit = useCallback(
    handleSubmit((data) => {
      postAlwinParams({ ...data, id_coll });
    }),
    [id_coll]
  );

  return (
    <MainSection className={'reset'}>
      <Card
        content={
          <>
            <form onSubmit={onSubmit}>
              <div className={styles.FormGrid}>{form}</div>
              <div className={'row justify-end mt-4'}>
                <Button type={'submit'}>{t('form:update')}</Button>
              </div>
            </form>
          </>
        }
      />
    </MainSection>
  );
}

export default AlwinParametersPage;
